import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { Component, Input, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-gantt-chart',
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss']
})

export class GanttChartComponent implements OnInit, OnChanges {

  /****************** Variable declaration ******************/
  sortedTimestamps: any = [];
  timestamps: any = {};
  divisor: any = 1;
  longestDuration: number = 0;
  selectedItem = '' //store the selected item on expand 
  jobId = '' // store the selected job id 
  jobBars: any = 0;   // job bars to display in the gantt chart
  barWidth: any;    // bar width as per the number of total bars

  /****************** Input and Output decorators ******************/
  @Input() data: any = []; // job details of a workflow
  @Input() startTime: any = ""; // start time of the complete workflow
  @Input() endTime: any = ""; // end time of the complete workflow
  @Input() qgData: any;

  /**
   * Constructor
   * @param elementRef
   * @param cf
   * @param datePipe
   */
  constructor(private elementRef: ElementRef,
    private cf: CommonFunctionalitiesService,
    private datePipe: DatePipe) { }

  /**ngOnInit */
  ngOnInit(): void {
    this.getTimestamps();
  }

  /**ngOnChanges */
  ngOnChanges(changes: SimpleChanges): void {
    this.resetVar();
    const mergedArray: any = []
    //take all steps from the jobs add the jobId and push each step to outer array
    this.data.forEach((item: any) => {
      let tempArr: any = []

      if (item?.steps) {
        tempArr = item.steps.map(((el1: any) => {
          el1 = { ...el1, job_id: item.id };
          el1.start = this.datePipe.transform(this.cf.getBrowserTime(el1.start), 'hh:mm:ss');
          el1.end = this.datePipe.transform(this.cf.getBrowserTime(el1.end), 'hh:mm:ss')
          return el1;
        }))
      }

      mergedArray.push(item);
      mergedArray.push(...tempArr);
    });

    this.data = mergedArray;

    this.getTimestamps();

    // code to modify data to see gantt chart heat map clearly
    // this.data = (this.data as any[]).map((val: any, index: number, arr: any[]) => {
    //   val.duration = arr.length - index;
    //   return val;
    // });

    this.longestDuration = this.data?.[0].duration;
    for (let data of this.data) {
      this.longestDuration = data.duration > this.longestDuration ? data.duration : this.longestDuration;
    }
  }

  /**getTimestamps function retrevies the timestamps and sorts the timestamp
   * assigning the bar width according to the total width/sortedTimestamps length
   */
  async getTimestamps() {
    await this.data?.map((d: any) => {
      let missingTimeSeq = this.generateTimeSequence(d.start, d.end);
      missingTimeSeq.forEach((t: string) => {
        this.timestamps[t] = 't';
      })
    });

    this.sortedTimestamps = Object.keys(this.timestamps).sort();
    this.divisor = (this.sortedTimestamps.length > 6) ? Math.ceil(this.sortedTimestamps.length / 6) : 1;
    this.barWidth = `${this.getTotalWidth() / (this.sortedTimestamps.length)}px`;
  }

  /**reset variables */
  resetVar() {
    this.sortedTimestamps = [];
    this.timestamps = {};
  }

  /**
    * generate missing time stamp by giving start and end time 
    * @param startTime 
    * @param endTime 
    * @returns returns an array of string ["08:01:48","08:01:49",....]
    */
  generateTimeSequence(startTime: any, endTime: any) {
    // Parse the start and end times
    const [startHour, startMinute, startSecond] = startTime.split(':').map(Number);
    const [endHour, endMinute, endSecond] = endTime.split(':').map(Number);

    // Convert the start time to seconds
    let currentTime = startHour * 3600 + startMinute * 60 + startSecond;

    // Convert the end time to seconds
    const endTimeInSeconds = endHour * 3600 + endMinute * 60 + endSecond;

    const timeSequence = [];

    while (currentTime <= endTimeInSeconds) {
      // Calculate hours, minutes, and seconds
      const hours = Math.floor(currentTime / 3600);
      const minutes = Math.floor((currentTime % 3600) / 60);
      const seconds = currentTime % 60;

      // Format the time as "HH:MM:SEC" and add it to the sequence
      const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      timeSequence.push(formattedTime);

      // Increment the current time by 1 second
      currentTime++;
    }

    return timeSequence;
  }

  /**getTotalWidth function is used to set the width of the bars and timestamp */
  getTotalWidth() {
    const rowMonthsWidth = (document.querySelector('.header') as HTMLElement)?.clientWidth || 0;
    const rowFirstWidth = (document.querySelector('.space') as HTMLElement)?.clientWidth || 0;
    const rowSecondWidth = (document.querySelector('.jobs') as HTMLElement)?.clientWidth || 0;
    return rowMonthsWidth - 100 - (rowFirstWidth + rowSecondWidth);
  }

  /**getTimestampToDisplay function is used to assign the timestamps to the header */

  getTimestampToDisplay(t: any, i: any) {
    if (i % this.divisor == 0) {
      return t;
    }
    return "";
  }

  getIndex(i: any) {
    if (i % this.divisor == 0 || this.sortedTimestamps.length < 4) {
      return 1;
    }
    return 0;
  }

  getSpanColumn(c: any) {
    let startCol = this.getNum(c.start) == 0 ? 1 : this.getNum(c.start);
    let endCol = this.getNum(c.end) == 0 ? 1 : this.getNum(c.end);

    return `${startCol} / ${endCol}`
  }

  getNum(s: any) {
    return this.sortedTimestamps.indexOf(s) + 1;
  }

  /**getDuration function retreives the time in proper format and the input time will be in seconds */
  async getDuration(d: any) {
    return this.cf.getDurationFromSeconds(d);
  }

  /**getTooltipText function is used to assign the mat-tool-tip to jobs, start time, end time */
  getTooltipText(data: any) {
    return `Job name: ${data?.label}
    Start time: ${data?.start}
    End time: ${data?.end}
    `
  }

  /**If quality gates success */
  isQualityGateSuccess(label: string): boolean {
    const qualityGateStatus = this.qgData[label];
    if (qualityGateStatus) {
      const statusList = qualityGateStatus.split(',').map((status: string) => status.trim());
      return statusList.every((status: string) => status === 'pass');
    }
    return false;
  }

  /**If quality gates fail */
  isQualityGateFail(label: string): boolean {
    const qualityGateStatus = this.qgData[label];
    if (qualityGateStatus) {
      const statusList = qualityGateStatus.split(',').map((status: string) => status.trim());
      return statusList.some((status: string) => status === 'fail');
    }
    return false;
  }

  /**Get tool tip for quality gates icon */
  getQualityGateTooltip(label: string): string {
    const qualityGateStatus = this.qgData[label];
    if (qualityGateStatus) {
      const statusList = qualityGateStatus.split(',').map((status: string) => status.trim());
      const totalChecks = statusList.length;
      const passedChecks = statusList.filter((status: string) => status === 'pass').length;
      return `${passedChecks}/${totalChecks} checks OK`;
    }
    return '';
  }

  getColor(dur: any) {
    return {
      'background-color': `hsl(39, 100%, ${100 - (+(((dur * 100) / this.longestDuration) / 2).toFixed(2))}%)`
    }
  }
}
